import React, { useCallback, useRef } from 'react';
import { marked } from 'marked';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Quote, Code2,
  Link as LinkIcon, Upload, Trash2,
  Table as TableIcon, Plus, Minus,
} from 'lucide-react';
import { API } from '../App';

// ── Toolbar button ────────────────────────────────────────────────────────────
function ToolBtn({ active, onClick, title, children }) {
  return (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        background: active ? 'var(--bg-4)' : 'transparent',
        border: 'none',
        borderRadius: 6,
        padding: '5px 6px',
        cursor: 'pointer',
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.1s, color 0.1s',
      }}
      tabIndex={-1}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-4)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px', flexShrink: 0 }} />;
}

// ── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ editor, onImageUpload }) {
  if (!editor) return null;

  function setLink() {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL:', prev || 'https://');
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }

  const ic = 14; // icon size

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
      padding: '6px 10px',
      background: 'var(--bg-2)',
      borderBottom: '1px solid var(--border)',
    }}>
      {/* Headings */}
      <ToolBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 size={ic} /></ToolBtn>
      <Sep />

      {/* Inline marks */}
      <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><UnderlineIcon size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><Strikethrough size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline code"><Code size={ic} /></ToolBtn>
      <Sep />

      {/* Alignment */}
      <ToolBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left"><AlignLeft size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Center"><AlignCenter size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right"><AlignRight size={ic} /></ToolBtn>
      <Sep />

      {/* Lists */}
      <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote"><Quote size={ic} /></ToolBtn>
      <ToolBtn active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block"><Code2 size={ic} /></ToolBtn>
      <Sep />

      {/* Link & image */}
      <ToolBtn active={editor.isActive('link')} onClick={setLink} title="Add link"><LinkIcon size={ic} /></ToolBtn>
      <ToolBtn active={false} onClick={onImageUpload} title="Upload image"><Upload size={ic} /></ToolBtn>
      <Sep />

      {/* Table controls */}
      <ToolBtn active={editor.isActive('table')} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon size={ic} /></ToolBtn>
      {editor.isActive('table') && (<>
        <ToolBtn active={false} onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column"><span style={{fontSize:10,fontWeight:700}}>C+</span></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column"><span style={{fontSize:10,fontWeight:700}}>C−</span></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row"><span style={{fontSize:10,fontWeight:700}}>R+</span></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row"><span style={{fontSize:10,fontWeight:700}}>R−</span></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table"><Trash2 size={ic} /></ToolBtn>
      </>)}
      <Sep />


    </div>
  );
}

// ── Rich editor ───────────────────────────────────────────────────────────────
export default function RichEditor({ content, onChange, color, placeholder }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      Image.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ['heading', 'paragraph', 'tableCell', 'tableHeader'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: placeholder || 'Start writing…' }),
    ],
    content: content ? (content.trimStart().startsWith('<') ? content : marked.parse(content)) : '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  const handleImageUpload = useCallback(async (file) => {
    if (!file || !editor) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const { url } = await fetch(`${API}/api/upload`, { method: 'POST', body: form }).then(r => r.json());
      const absUrl = url.startsWith('http') ? url : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
      editor.chain().focus().setImage({ src: absUrl }).run();
    } catch (err) { console.error('Image upload failed', err); }
  }, [editor]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) handleImageUpload(e.target.files[0]); e.target.value = ''; }} />

      <Toolbar editor={editor} onImageUpload={() => fileInputRef.current?.click()} />

      <EditorContent editor={editor} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }} />

      <style>{`
        .ProseMirror {
          outline: none;
          padding: 16px 20px;
          min-height: 220px;
          font-size: 14px;
          line-height: 1.75;
          color: var(--text-primary, #e0e0e0);
          font-family: inherit;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: var(--text-faint, #555);
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror h1 { font-size: 1.5em; font-weight: 700; margin: 0.6em 0 0.3em; }
        .ProseMirror h2 { font-size: 1.25em; font-weight: 700; margin: 0.6em 0 0.3em; }
        .ProseMirror h3 { font-size: 1.1em; font-weight: 600; margin: 0.5em 0 0.2em; }
        .ProseMirror ul { list-style: disc; padding-left: 1.4em; margin: 0.4em 0; }
        .ProseMirror ol { list-style: decimal; padding-left: 1.4em; margin: 0.4em 0; }
        .ProseMirror li { margin: 0.15em 0; }
        .ProseMirror blockquote { border-left: 3px solid var(--border, #555); padding-left: 1em; opacity: 0.75; margin: 0.5em 0; }
        .ProseMirror code { background: var(--bg-4, #2a2a3e); padding: 0.1em 0.4em; border-radius: 4px; font-size: 0.85em; font-family: monospace; }
        .ProseMirror pre { background: var(--bg-4, #2a2a3e); padding: 0.8em 1em; border-radius: 8px; overflow-x: auto; margin: 0.5em 0; }
        .ProseMirror pre code { background: none; padding: 0; }
        .ProseMirror a { color: #818cf8; text-decoration: underline; cursor: pointer; }
        .ProseMirror img { max-width: 100%; border-radius: 8px; margin: 0.5em 0; display: block; }
        .ProseMirror table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
        .ProseMirror th, .ProseMirror td { border: 1px solid var(--border, #333); padding: 6px 10px; text-align: left; min-width: 60px; }
        .ProseMirror th { background: var(--bg-4, #2a2a3e); font-weight: 600; }
        .ProseMirror .selectedCell { background: rgba(99,102,241,0.15); }
        .ProseMirror p { margin: 0.3em 0; }
        .ProseMirror p[style*="text-align: center"] { text-align: center; }
        .ProseMirror p[style*="text-align: right"] { text-align: right; }
      `}</style>
    </div>
  );
}
